import { evalExpressions } from '@form-engine/core-util/expressions';
import FormEngine from '..';
import {
    ElementDefinition,
    ElementDefinitionValidationProps,
    FormSession,
    ValidationType,
} from '../types/form';

/**
 * converts string values containing `string[][]` json data, to a one dimensional
 * comma separated list, so validation functions can work with it.
 * @example
 *  concatNestedElementValue('[[],[]]'); // => ''
 *  concatNestedElementValue('[["List A - Value A", "List A - Value B"],["List B - Value A", "List B - Value B"]]');
 *  // => 'List A - Value A, List A - Value B, List B - Value A, List B - Value B'
 * @param rawValue - json encoded input value (of nested lists)
 * @returns comma separated list of flattened values
 */
function concatNestedElementValue(rawValue: string) {
    if (!rawValue) {
        return '';
    }

    let value;
    try {
        value = JSON.parse(rawValue);
    } catch {
        console.warn('failed to parse nested element value during validation');
        return '';
    }

    if (!Array.isArray(value)) {
        console.warn(
            'unexpected nested element value, expected array, got ',
            typeof value
        );
        return '';
    }

    if (!value.length) {
        return '';
    }

    const concatenatedValues = [];

    for (const group of value) {
        if (Array.isArray(group)) {
            concatenatedValues.push(...group);
        }
    }

    // regular lists values are expected to be simple comma separated lists,
    // so we treat nested lists the same way during validation
    return concatenatedValues.join(',');
}

export default (function validate(
    element: ElementDefinition & ElementDefinitionValidationProps,
    value: string,
    session: FormSession
) {
    const validationFunctions = Object.values(FormEngine.validations);

    // when the value of the element is expected to be nested list (e.g. multi checkbox)
    // we need to concat the json value before checking it
    if (element.nested) {
        value = concatNestedElementValue(value);
    }

    for (const validation of validationFunctions) {
        const error: ReturnType<ValidationType> = validation(
            element,
            value ?? '',
            session
        );
        if (error !== null) return error;
    }

    let required = element.required;

    if (!required && element.requiredExpressions?.length) {
        required = evalExpressions(
            element.requiredExpressions,
            session.locals ?? {}
        );
    }

    if (
        required &&
        String(value || '') === '' &&
        !session?.elements?.[element.id]?.loading
    ) {
        return FormEngine.i18n.fallback(
            element.requiredMessage,
            'validation.required',
            {
                label: element.label || element.name,
            }
        );
    }

    return null;
} as ValidationType);
